import React from "react";
import dayjs, { Dayjs } from "dayjs";

import WorkDay from "../../Types/WorkDay";
import WorkDayService from "../../Services/WorkDayService";
import { Functions } from "../../Types/Enums/Functions";
import WorkDayAddWorkerDialog from "./WorkDayAddWorkerDialog";

interface Props {
    workDay: WorkDay | null;
    selectedDate: Dayjs | null;
    selectedPeriodId: string | null;
    onUpdated: (workDay: WorkDay) => void;
}

const ACCENT = "#c79656";
const TEXT = "#0f172a";
const TEXT_MUTED = "rgba(0,0,0,0.50)";
const BORDER = "rgba(0,0,0,0.08)";
const COL = "minmax(180px, 1.5fr) repeat(3, 1fr)";

export default class WorkDayWorkersOverview extends React.Component<Props> {
    private fmt(n: number) {
        return new Intl.NumberFormat(undefined, { style: "currency", currency: "EUR", maximumFractionDigits: 2 }).format(n || 0);
    }

    private initials(first: string, last: string) {
        return ((first?.[0] ?? "") + (last?.[0] ?? "")).toUpperCase();
    }

    public render() {
        const periods = WorkDayService.getDisplayPeriods(this.props.workDay);
        const selectedPeriod = periods.find((period) => period.Id === this.props.selectedPeriodId) ?? null;
        const workers = selectedPeriod?.Workers ?? [];
        const workerCount = workers.length;
        const perWorkerCash = workerCount > 0 ? (selectedPeriod?.TipCash ?? 0) / workerCount : 0;
        const perWorkerPin = workerCount > 0 ? (selectedPeriod?.TipPin ?? 0) / workerCount : 0;

        return (
            <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", gap: 12 }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexShrink: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <div style={{ width: 4, height: 20, borderRadius: 4, background: ACCENT }} />
                        <span style={{ fontSize: 16, fontWeight: 800, color: TEXT }}>Workers</span>
                    </div>

                    {selectedPeriod != null ? (
                        <WorkDayAddWorkerDialog
                            workerList={workers}
                            workDay={this.props.workDay ?? this.props.selectedDate ?? dayjs()}
                            selectedPeriodId={selectedPeriod.Id}
                            buttonLabel="Add workers"
                            onClose={(newWorkDay) => this.props.onUpdated(newWorkDay)}
                        />
                    ) : null}
                </div>

                <div
                    style={{
                        flex: 1,
                        minHeight: 0,
                        display: "flex",
                        flexDirection: "column",
                        border: `1px solid ${BORDER}`,
                        borderRadius: 12,
                        overflow: "hidden",
                        background: "white",
                    }}
                >
                    <div
                        style={{
                            display: "grid",
                            gridTemplateColumns: COL,
                            gap: 12,
                            padding: "10px 14px",
                            background: "rgba(0,0,0,0.025)",
                            borderBottom: `1px solid ${BORDER}`,
                            flexShrink: 0,
                        }}
                    >
                        {["Worker", "Cash", "PIN", "Total"].map((heading, index) => (
                            <div
                                key={heading}
                                style={{
                                    fontSize: 11,
                                    fontWeight: 700,
                                    letterSpacing: "0.4px",
                                    textTransform: "uppercase",
                                    color: TEXT_MUTED,
                                    textAlign: index > 0 ? "right" : "left",
                                }}
                            >
                                {heading}
                            </div>
                        ))}
                    </div>

                    {selectedPeriod == null ? (
                        <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", color: TEXT_MUTED, fontSize: 13 }}>
                            Select a work period on the left.
                        </div>
                    ) : workerCount === 0 ? (
                        <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", color: TEXT_MUTED, fontSize: 13 }}>
                            No workers in this work period.
                        </div>
                    ) : (
                        <>
                            <div style={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
                                {workers.map((worker, index) => {
                                    const rowTotal = perWorkerCash + perWorkerPin;

                                    return (
                                        <div
                                            key={worker.Guid}
                                            style={{
                                                display: "grid",
                                                gridTemplateColumns: COL,
                                                gap: 12,
                                                padding: "10px 14px",
                                                alignItems: "center",
                                                borderTop: index === 0 ? "none" : `1px solid ${BORDER}`,
                                            }}
                                        >
                                            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                                <div
                                                    style={{
                                                        width: 30,
                                                        height: 30,
                                                        borderRadius: 8,
                                                        background: "rgba(0,0,0,0.04)",
                                                        border: `1px solid ${BORDER}`,
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                        fontSize: 11,
                                                        fontWeight: 800,
                                                        color: TEXT,
                                                        flexShrink: 0,
                                                    }}
                                                >
                                                    {this.initials(worker.FirstName, worker.LastName)}
                                                </div>

                                                <div style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
                                                    <span style={{ fontWeight: 700, color: TEXT, fontSize: 13, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                                        {worker.FirstName} {worker.LastName}
                                                    </span>
                                                    <span style={{ fontSize: 11, color: TEXT_MUTED }}>
                                                        {Functions[worker.Function] || "—"}
                                                    </span>
                                                </div>
                                            </div>

                                            <div style={{ textAlign: "right", fontSize: 13, fontWeight: 600, color: TEXT_MUTED }}>
                                                {this.fmt(perWorkerCash)}
                                            </div>
                                            <div style={{ textAlign: "right", fontSize: 13, fontWeight: 600, color: TEXT_MUTED }}>
                                                {this.fmt(perWorkerPin)}
                                            </div>
                                            <div style={{ textAlign: "right", fontSize: 13, fontWeight: 800, color: TEXT }}>
                                                {this.fmt(rowTotal)}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>

                            <div
                                style={{
                                    display: "grid",
                                    gridTemplateColumns: COL,
                                    gap: 12,
                                    padding: "10px 14px",
                                    borderTop: `1px solid ${BORDER}`,
                                    background: "rgba(0,0,0,0.025)",
                                    flexShrink: 0,
                                }}
                            >
                                <div style={{ fontSize: 12, fontWeight: 700, color: TEXT_MUTED, textTransform: "uppercase", letterSpacing: "0.4px" }}>
                                    Period total
                                </div>
                                <div style={{ textAlign: "right", fontSize: 13, fontWeight: 700, color: TEXT }}>
                                    {this.fmt(selectedPeriod.TipCash ?? 0)}
                                </div>
                                <div style={{ textAlign: "right", fontSize: 13, fontWeight: 700, color: TEXT }}>
                                    {this.fmt(selectedPeriod.TipPin ?? 0)}
                                </div>
                                <div style={{ textAlign: "right", fontSize: 13, fontWeight: 800, color: ACCENT }}>
                                    {this.fmt((selectedPeriod.TipCash ?? 0) + (selectedPeriod.TipPin ?? 0))}
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>
        );
    }
}
